// ForgotPassword.jsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';

// ============ ANIMATIONS ============
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-30px); }
`;

// ============ STYLED COMPONENTS ============
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 500ms ease-out;
`;

const BackgroundOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
  pointer-events: none;
  
  ${props => props.$variant === 'primary' && css`
    width: 500px;
    height: 500px;
    background: rgba(14, 165, 233, 0.2);
    top: -100px;
    right: -100px;
    animation: ${float} 20s ease-in-out infinite;
  `}
  
  ${props => props.$variant === 'secondary' && css`
    width: 400px;
    height: 400px;
    background: rgba(139, 92, 246, 0.15);
    bottom: -50px;
    left: -50px;
    animation: ${float} 15s ease-in-out infinite reverse;
  `}
`;

const Content = styled.div`
  width: 100%;
  max-width: 900px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 10;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 60px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  max-width: 500px;
  width: 100%;
  animation: ${fadeInUp} 600ms cubic-bezier(0.34, 1.56, 0.64, 1);

  @media (max-width: 768px) {
    padding: 40px 24px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
`;

const Title = styled.h1`
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 200ms;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
    background: #f0f9ff;
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(14, 165, 233, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
    animation: ${props => props.$isLoading ? `spin 1s linear infinite` : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background: #ecfdf5;
  border: 1px solid #10b981;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 24px;
  
  .title {
    font-weight: 600;
    color: #047857;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .message {
    color: #059669;
    font-size: 14px;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #ef4444;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 24px;

  .title {
    font-weight: 600;
    color: #dc2626;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .message {
    color: #ef4444;
    font-size: 14px;
  }
`;

const Footer = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
  color: #64748b;

  a {
    color: #0ea5e9;
    text-decoration: none;
    font-weight: 600;
    transition: color 200ms;

    &:hover {
      color: #0284c7;
    }
  }
`;

const InfoBox = styled.div`
  background: #eff6ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  padding: 16px;
  margin-top: 24px;
  font-size: 14px;
  color: #0c4a6e;
  line-height: 1.6;

  strong {
    display: block;
    margin-bottom: 8px;
    color: #0369a1;
  }
`;

// ============ ICON COMPONENTS ============
const Icons = {
  Mail: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 6l-10 5L2 6" />
    </svg>
  ),
  Loader: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  AlertCircle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
};

// ============ FORGOT PASSWORD COMPONENT ============
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
      } else {
        setError(data.message || 'Failed to send reset link. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <BackgroundOrb $variant="primary" />
      <BackgroundOrb $variant="secondary" />

      <Content>
        <Card>
          <Header>
            <IconWrapper>
              <Icons.Mail />
            </IconWrapper>
            <Title>Reset Password</Title>
            <Subtitle>
              Enter your email address and we'll send you a link to reset your password
            </Subtitle>
          </Header>

          {isSubmitted ? (
            <>
              <SuccessMessage>
                <div className="title">
                  <Icons.Check />
                  Check your email
                </div>
                <div className="message">
                  We've sent a password reset link to <strong>{email}</strong>. The link expires in 24 hours.
                </div>
              </SuccessMessage>

              <InfoBox>
                <strong>What's next?</strong>
                1. Check your email (including spam folder)<br />
                2. Click the reset link<br />
                3. Enter your new password<br />
                <br />
                Didn't receive the email? Check if you used the correct email address, or try requesting a new link.
              </InfoBox>

              <Footer style={{ marginTop: '32px' }}>
                <button
                  onClick={() => setIsSubmitted(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0ea5e9',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}
                >
                  Try another email
                </button>
                <div style={{ marginTop: '12px' }}>
                  Remember your password? <Link to="/login">Back to Login</Link>
                </div>
              </Footer>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <ErrorMessage>
                  <div className="title">
                    <Icons.AlertCircle />
                    Error
                  </div>
                  <div className="message">{error}</div>
                </ErrorMessage>
              )}

              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  disabled={isLoading}
                  autoFocus
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isLoading} $isLoading={isLoading}>
                {isLoading ? (
                  <>
                    <Icons.Loader />
                    Sending link...
                  </>
                ) : (
                  <>
                    <Icons.Mail />
                    Send Reset Link
                  </>
                )}
              </SubmitButton>

              <Footer>
                Remember your password? <Link to="/login">Back to Login</Link>
              </Footer>
            </form>
          )}
        </Card>
      </Content>
    </PageContainer>
  );
};

export default ForgotPassword;
